// Paperclip's `default_url` (config/paperclip.yml) — the URL a `*_url` field takes when no file is
// attached. Prefer `isMissingImage` in `@/utils/isMissingImage` over comparing against this directly.
export const MISSING_IMAGE_URL = '/missing.png';

export const COLORS = {
  black: '#000',
  blue: '#2b9ab8',
  'checkbox-blue': '#1b63dc',
  'dark-green': '#2A8806',
  'dark-grey': '#777770',
  'dark-orange': '#d14801',
  'dark-purple': '#3a1f6a',
  'focus-purple': '#9747ff',
  green: '#2A8806',
  grey: '#cccccc',
  info: '#3FA2F7',
  'light-grey': '#eee',
  'light-orange': '#fef7ee',
  'light-saffron': '#fff1c2',
  'med-dark-grey': '#898989',
  'med-grey': '#ccc',
  orange: '#f69c05',
  red: '#d2232a',
  saffron: '#f3c03f',
  'tci-black': '#3c3c3c',
  'tci-purple': '#522e91',
  'teal-blue': '#247f99',
  'very-dark-grey': '#555',
  white: '#ffffff',
  yellow: '#ffca17',
};

// AIDEV-NOTE: Sysadmin SPA semantic color tokens backed by CSS custom properties.
// Defined in _sysadmin_spa.scss with dark defaults + prefers-color-scheme light overrides.
// Usage: tw-bg-spa-bg, tw-text-spa-heading, tw-border-spa-border, tw-text-spa-accent-text, etc.
export const SPA_COLORS = {
  spa: {
    // Backgrounds (tw-bg-spa-bg, tw-bg-spa-bg-secondary, etc.)
    bg: 'var(--spa-bg)',
    'bg-secondary': 'var(--spa-bg-secondary)',
    'bg-tertiary': 'var(--spa-bg-tertiary)',
    'bg-hover': 'var(--spa-bg-hover)',
    'bg-active': 'var(--spa-bg-active)',
    'bg-stripe-even': 'var(--spa-bg-stripe-even)',
    'bg-stripe-odd': 'var(--spa-bg-stripe-odd)',
    'bg-skeleton': 'var(--spa-bg-skeleton)',
    'bg-skeleton-subtle': 'var(--spa-bg-skeleton-subtle)',
    'bg-code': 'var(--spa-bg-code)',
    'bg-disabled': 'var(--spa-bg-disabled)',
    // Text (tw-text-spa-heading, tw-text-spa-primary, etc.)
    heading: 'var(--spa-text-heading)',
    primary: 'var(--spa-text-primary)',
    secondary: 'var(--spa-text-secondary)',
    tertiary: 'var(--spa-text-tertiary)',
    muted: 'var(--spa-text-muted)',
    disabled: 'var(--spa-text-disabled)',
    'on-bg': 'var(--spa-text-on-bg)',
    'high-contrast': 'var(--spa-text-white)',
    error: 'var(--spa-text-error)',
    info: 'var(--spa-text-info)',
    success: 'var(--spa-text-success)',
    warning: 'var(--spa-text-warning)',
    // Borders (tw-border-spa-border, tw-border-spa-border-secondary, etc.)
    border: 'var(--spa-border)',
    'border-secondary': 'var(--spa-border-secondary)',
    'border-hover': 'var(--spa-border-hover)',
    'border-input': 'var(--spa-border-input)',
    'border-tab-hover': 'var(--spa-border-tab-hover)',
    // Accent (tw-text-spa-accent-text, tw-bg-spa-accent-bg, tw-border-spa-accent, etc.)
    accent: 'var(--spa-accent)',
    'accent-solid': 'var(--spa-accent-solid)',
    'accent-solid-hover': 'var(--spa-accent-solid-hover)',
    'accent-text': 'var(--spa-accent-text)',
    'accent-text-hover': 'var(--spa-accent-text-hover)',
    'accent-bg': 'var(--spa-accent-bg)',
    'accent-bg-hover': 'var(--spa-accent-bg-hover)',
    'accent-bg-subtle': 'var(--spa-accent-bg-subtle)',
    // Destructive actions (tw-bg-spa-danger-solid, tw-border-spa-danger-solid)
    'danger-solid': 'var(--spa-danger-solid)',
    'danger-solid-hover': 'var(--spa-danger-solid-hover)',
    'logo-fill': 'var(--spa-logo-fill)',
  },
};

// AIDEV-NOTE: buttonFocusRing rings the element itself. For an icon button whose hit area is
// deliberately bigger than its glyph (WCAG 2.5.8), that leaves the glyph sitting off-center inside
// the ring — so this variant rings the child <svg> instead. Same colors as buttonFocusRing.
export const glyphFocusRing = [
  // Scoped to :focus, not :focus-visible — `button:focus { outline: 5px auto }` in _reset.scss is
  // (0,1,1), so it outranks an unqualified reset and shows a native ring on mouse click.
  'focus:tw-outline-none',
  '[&:focus-visible>svg]:tw-outline',
  '[&:focus-visible>svg]:tw-outline-[2.5px]',
  '[&:focus-visible>svg]:tw-outline-tci-purple',
  '[&:focus-visible>svg]:tw-outline-offset-2',
  '[&:focus-visible>svg]:tw-rounded',
  '[&:focus-visible>svg]:tw-shadow-[0_0_0_2px_white,0_0_4px_4.5px_rgba(151,71,255,0.55)]',
].join(' ');

// AIDEV-NOTE: Mirrors the `button-focus` SCSS mixin in app/assets/stylesheets/abstracts/_mixins.scss.
// Outline + white separator ring + outer purple glow via a ::before. Host must be a positioning
// context; `tw-relative` is included.
export const buttonFocusRing = [
  'tw-relative',
  'focus-visible:tw-outline focus-visible:tw-outline-2',
  'focus-visible:tw-outline-tci-purple focus-visible:tw-outline-offset-2',
  'focus-visible:tw-shadow-[0_0_0_2px_white]',
  "focus-visible:before:tw-content-['']",
  'focus-visible:before:tw-absolute focus-visible:before:tw-inset-[-4px]',
  'focus-visible:before:tw-rounded-lg',
  'focus-visible:before:tw-shadow-[0_0_4px_theme(colors.focus-purple)]',
  'focus-visible:before:tw-pointer-events-none',
].join(' ');
